import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '@/lib/getApiUrl';
// CB:BEGIN stripe-shared-ui-imports
import SettingsCard from '@/components/ui/SettingsCard';
// CB:END stripe-shared-ui-imports
import { FaCheckCircle, FaExclamationTriangle, FaExternalLinkAlt } from 'react-icons/fa';
import stripePromise from '@/lib/stripe';

interface VerificationStatus {
  verified: boolean;
  accountId?: string;
  customerId?: string;
  detailsSubmitted?: boolean;
  chargesEnabled?: boolean;
  hasSecretKey?: boolean;
  hasWebhookSecret?: boolean;
  testMode?: boolean;
  dashboard?: string;
}

export default function StripeConnectDemo() {
  const [contractorStatus, setContractorStatus] = useState<VerificationStatus>({ verified: false });
  const [homeownerStatus, setHomeownerStatus] = useState<VerificationStatus>({ verified: false });
  const [platformStatus, setPlatformStatus] = useState<VerificationStatus>({ verified: false });
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [onboardingUrl, setOnboardingUrl] = useState<string>('');
  const [homeownerHasPaymentMethod, setHomeownerHasPaymentMethod] = useState<boolean>(false);

  const apiBaseUrl = getApiBaseUrl();

  // Poll verification status
  useEffect(() => {
    const pollStatus = async () => {
      try {
        const [contractorRes, homeownerRes, platformRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/demo/stripe/contractor/status`),
          fetch(`${apiBaseUrl}/api/demo/stripe/homeowner/status`),
          fetch(`${apiBaseUrl}/api/demo/stripe/platform/status`)
        ]);

        const contractor = await contractorRes.json();
        const homeowner = await homeownerRes.json();
        const platform = await platformRes.json();

        setContractorStatus(contractor);
        setHomeownerStatus(homeowner);
        setPlatformStatus(platform);
      } catch (error) {
        console.error('Status polling error:', error);
      }
    };

    pollStatus();
    const interval = setInterval(pollStatus, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleContractorConnect = async () => {
    setLoading(prev => ({ ...prev, contractor: true }));
    try {
      const response = await fetch(`${apiBaseUrl}/api/demo/stripe/contractor/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'contractor@demo.com' })
      });
      const data = await response.json();
      setOnboardingUrl(data.onboardingUrl);
      window.open(data.onboardingUrl, '_blank');
    } catch (error) {
      console.error('Contractor creation error:', error);
    } finally {
      setLoading(prev => ({ ...prev, contractor: false }));
    }
  };

  const handleHomeownerConnect = async () => {
    setLoading(prev => ({ ...prev, homeowner: true }));
    try {
      const response = await fetch(`${apiBaseUrl}/api/demo/stripe/homeowner/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'homeowner@demo.com' })
      });
      const data = await response.json();
      console.log('Homeowner created:', data);
      
      if (data.customerId) {
        setHomeownerStatus({ verified: true, dashboard: data.customerId });
        await checkPaymentMethods();
      }
    } catch (error) {
      console.error('Homeowner creation error:', error);
    } finally {
      setLoading(prev => ({ ...prev, homeowner: false }));
    }
  };

  const checkPaymentMethods = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/demo/stripe/homeowner/payment-methods`);
      const data = await response.json();
      setHomeownerHasPaymentMethod(data.hasPaymentMethod);
    } catch (error) {
      console.error('Error checking payment methods:', error);
    }
  };

  const handleLinkBankAccount = async () => {
    if (!homeownerStatus.customerId) {
      alert('Please connect homeowner first');
      return;
    }

    setLoading(prev => ({ ...prev, bankLink: true }));
    try {
      // Create Financial Connections session
      const sessionResponse = await fetch(`${apiBaseUrl}/api/demo/stripe/homeowner/financial-connections/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: homeownerStatus.customerId })
      });
      
      if (!sessionResponse.ok) {
        const errorData = await sessionResponse.json();
        throw new Error(errorData.error || 'Failed to create Financial Connections session');
      }
      
      const sessionData = await sessionResponse.json();
      
      if (sessionData.client_secret) {
        // Use shared Stripe instance (null over HTTP unless VITE_DEV_STRIPE_HTTP=true)
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Failed to load Stripe');
        }

        // Present Financial Connections modal
        const { error } = await stripe.financialConnections.present({
          clientSecret: sessionData.client_secret
        });

        if (error) {
          console.error('Financial Connections error:', error);
          alert(`Error: ${error.message}`);
          return;
        }

        // Verify bank account was linked
        const verifyResponse = await fetch(`${apiBaseUrl}/api/demo/stripe/homeowner/verify-bank`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId: homeownerStatus.customerId })
        });

        if (verifyResponse.ok) {
          const verifyData = await verifyResponse.json();
          setHomeownerHasPaymentMethod(true);
          alert(`Bank account linked successfully! Account ending in ${verifyData.bankAccount?.last4}`);
        } else {
          const errorData = await verifyResponse.json();
          alert(`Bank account verification failed: ${errorData.error}`);
        }
      }
    } catch (error) {
      console.error('Error creating financial session:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(prev => ({ ...prev, bankLink: false }));
    }
  };

  const handlePlatformConnect = () => {
    // Platform verification is automatic based on environment variables
    console.log('Platform status:', platformStatus);
  };

  const allVerified = contractorStatus.verified && homeownerStatus.verified && platformStatus.verified && homeownerHasPaymentMethod;

  const handleContinue = () => {
    if (allVerified) {
      // Navigate to demo payments page using the app's routing system
      window.history.pushState({ screen: 'demo-payments' }, '', '/demo/payments');
      window.location.reload(); // Reload to trigger the route change
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header with blue theme */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-blue-900">Stripe Connect Demo</h1>
                <p className="text-sm md:text-base text-blue-800/80 mt-1">Contractor onboarding, homeowner setup, and platform verification.</p>
              </div>
              {/* Copyable CLI */}
              <div className="w-full md:w-auto">
                <div className="bg-white/80 backdrop-blur border border-blue-200 rounded-xl px-3 py-2 text-sm text-blue-900 flex items-center gap-2">
                  <code className="font-mono">npm run stripe:listen</code>
                  <button
                    onClick={() => navigator.clipboard.writeText('npm run stripe:listen')}
                    className="ml-2 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >Copy</button>
                </div>
              </div>
            </div>
            {/* Stepper */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2">
              {[
                { label: 'Contractor', done: contractorStatus.verified },
                { label: 'Homeowner', done: homeownerStatus.verified },
                { label: 'Bank/Card', done: homeownerHasPaymentMethod },
                { label: 'Platform', done: platformStatus.verified },
              ].map((s, i) => (
                <div key={i} className={`rounded-lg px-3 py-2 text-sm font-medium border ${s.done ? 'bg-green-50 text-green-800 border-green-200' : 'bg-white text-blue-800 border-blue-200'}`}>
                  {s.done ? '✓' : (i+1)} {s.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Verification Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Contractor Card */}
          {/* CB:BEGIN stripe-card-contractor */}
          <SettingsCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Contractor</h3>
              {contractorStatus.verified ? (
                <FaCheckCircle className="h-6 w-6 text-green-500" />
              ) : (
                <FaExclamationTriangle className="h-6 w-6 text-yellow-500" />
              )}
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Express account onboarding for contractors to receive payments.
            </p>

            {contractorStatus.verified ? (
              <div className="space-y-2">
                <div className="text-sm text-green-600 font-medium">
                  ✓ Contractor verified
                </div>
                <div className="text-xs text-gray-500">
                  Account: {contractorStatus.accountId?.slice(0, 20)}...
                </div>
                {contractorStatus.dashboard && (
                  <a
                    href={contractorStatus.dashboard}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
                  >
                    View Dashboard <FaExternalLinkAlt className="h-3 w-3 ml-1" />
                  </a>
                )}
              </div>
            ) : (
              <button
                onClick={handleContractorConnect}
                disabled={loading.contractor}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading.contractor ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  'Connect Contractor Here'
                )}
              </button>
            )}
          </SettingsCard>
          {/* CB:END stripe-card-contractor */}

          {/* Homeowner Card */}
          {/* CB:BEGIN stripe-card-homeowner */}
          <SettingsCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Homeowner</h3>
              {homeownerStatus.verified ? (
                <FaCheckCircle className="h-6 w-6 text-green-500" />
              ) : (
                <FaExclamationTriangle className="h-6 w-6 text-yellow-500" />
              )}
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Customer account setup for homeowners to make payments.
            </p>

            {homeownerStatus.verified ? (
              <div className="space-y-3">
                <div className="text-sm text-green-600 font-medium">
                  ✓ Homeowner verified
                </div>
                <div className="text-xs text-gray-500">
                  Customer: {homeownerStatus.customerId?.slice(0, 20)}...
                </div>
                
                {homeownerHasPaymentMethod ? (
                  <div className="text-sm text-green-600 font-medium">
                    ✓ Bank account linked
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-sm text-yellow-600 font-medium">
                      ⚠ Add payment method required
                    </div>
                    <button
                      onClick={handleLinkBankAccount}
                      disabled={loading.bankLink}
                      className="w-full bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
                    >
                      {loading.bankLink ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        'Link Bank Account (Test)'
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleHomeownerConnect}
                disabled={loading.homeowner}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading.homeowner ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  'Connect Homeowner Here'
                )}
              </button>
            )}
          </SettingsCard>
          {/* CB:END stripe-card-homeowner */}

          {/* CoreBids Platform Card */}
          {/* CB:BEGIN stripe-card-platform */}
          <SettingsCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">CoreBids</h3>
              {platformStatus.verified ? (
                <FaCheckCircle className="h-6 w-6 text-green-500" />
              ) : (
                <FaExclamationTriangle className="h-6 w-6 text-yellow-500" />
              )}
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Platform verification for Stripe keys and webhook configuration.
            </p>

            {platformStatus.verified ? (
              <div className="space-y-2">
                <div className="text-sm text-green-600 font-medium">
                  ✓ CoreBids verified
                </div>
                <div className="text-xs text-gray-500">
                  Test Mode: {platformStatus.testMode ? 'Enabled' : 'Disabled'}
                </div>
                <div className="text-xs text-gray-500">
                  Webhook: {platformStatus.hasWebhookSecret ? 'Configured' : 'Missing'}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-red-600">
                  {!platformStatus.hasSecretKey && '• Missing Stripe Secret Key'}
                </div>
                <div className="text-xs text-red-600">
                  {!platformStatus.hasWebhookSecret && '• Missing Webhook Secret'}
                </div>
                <div className="text-xs text-gray-500">
                  Check environment configuration
                </div>
              </div>
            )}
          </SettingsCard>
          {/* CB:END stripe-card-platform */}
        </div>

        {/* Continue Button */}
        <div className="text-center">
          <button
            onClick={handleContinue}
            disabled={!allVerified}
            className={`px-8 py-3 rounded-lg font-semibold text-lg ${
              allVerified
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {allVerified ? 'Continue to Demo Payments' : 'Complete All Verifications'}
          </button>
          
          {!allVerified && (
            <p className="text-sm text-gray-500 mt-4">
              All three parties must be verified before proceeding
            </p>
          )}
        </div>

        {/* Instructions */}
        {/* CB:BEGIN stripe-card-instructions */}
        <SettingsCard className="mt-12">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Instructions</h3>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex items-start">
              <span className="font-medium text-blue-600 mr-2">1.</span>
              <span>Click "Connect Contractor Here" to open Stripe Express onboarding in a new tab</span>
            </div>
            <div className="flex items-start">
              <span className="font-medium text-blue-600 mr-2">2.</span>
              <span>Complete the contractor onboarding form (use test data)</span>
            </div>
            <div className="flex items-start">
              <span className="font-medium text-blue-600 mr-2">3.</span>
              <span>Add a bank account during contractor onboarding (required for payouts)</span>
            </div>
            <div className="flex items-start">
              <span className="font-medium text-blue-600 mr-2">4.</span>
              <span>Click "Connect Homeowner Here" to create a test customer</span>
            </div>
            <div className="flex items-start">
              <span className="font-medium text-blue-600 mr-2">5.</span>
              <span>Add a payment method for the homeowner (required for deposits)</span>
            </div>
            <div className="flex items-start">
              <span className="font-medium text-blue-600 mr-2">6.</span>
              <span>Verify CoreBids platform configuration (automatic)</span>
            </div>
            <div className="flex items-start">
              <span className="font-medium text-blue-600 mr-2">7.</span>
              <span>Click "Continue to Demo Payments" when all are verified</span>
            </div>
          </div>
        </SettingsCard>
        {/* CB:END stripe-card-instructions */}
      </div>
    </div>
  );
}
